import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { useTheme, themes } from '../ThemeProvider';
import { useAppSelector } from '../../store/store';
import { useUserDisplay } from '../../hooks/useUserDisplay';
import { Circle, Heart, MessageCircle, User, MapPin, Briefcase, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

export function LiveTab() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const { getProfilePhoto, getFullName, getInitials } = useUserDisplay();
  const { onlineUsers } = useAppSelector(state => state.chat);
  const [liveProfiles, setLiveProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // For demo purposes, we'll show online users from chat
  // In production, you would fetch actual live/active profiles
  useEffect(() => {
    // Mock live profiles for now
    const mockLiveProfiles = [
      {
        _id: '1',
        firstName: 'Priya',
        lastName: 'Sharma',
        age: 26,
        city: 'Mumbai',
        profession: 'Software Engineer',
        education: 'B.Tech',
        profilePicture: '',
        isOnline: true,
      },
      {
        _id: '2',
        firstName: 'Rahul',
        lastName: 'Patel',
        age: 28,
        city: 'Delhi',
        profession: 'Doctor',
        education: 'MBBS',
        profilePicture: '',
        isOnline: true,
      },
      {
        _id: '3',
        firstName: 'Ananya',
        lastName: 'Reddy',
        age: 25,
        city: 'Bangalore',
        profession: 'Designer',
        education: 'B.Des',
        profilePicture: '',
        isOnline: true,
      },
      {
        _id: '4',
        firstName: 'Vikram',
        lastName: 'Singh',
        age: 30,
        city: 'Pune',
        profession: 'Business Analyst',
        education: 'MBA',
        profilePicture: '',
        isOnline: true,
      },
    ];
    setLiveProfiles(mockLiveProfiles);
  }, [onlineUsers]);

  const handleInterest = (profileId: string) => {
    toast.success('Interest sent successfully!');
  };

  const handleMessage = (profileId: string) => {
    toast.info('Opening chat...');
  };

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl" style={{ color: currentTheme.primary }}>
            Live Profiles
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Members currently active on the platform
          </p>
        </div>
        <Badge className="gap-1" style={{ backgroundColor: currentTheme.primary }}>
          <Circle className="h-2 w-2 fill-white" />
          {liveProfiles.length} Online
        </Badge>
      </div>

      {/* Live Profiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {liveProfiles.map((profile) => (
          <Card 
            key={profile._id} 
            className="hover:shadow-lg transition-shadow cursor-pointer relative"
            onClick={() => onViewProfile?.(profile)}
          >
            {/* Online Indicator */}
            <div className="absolute top-3 right-3 z-10">
              <div className="flex items-center gap-1 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                <Circle className="h-2 w-2 fill-white animate-pulse" />
                Online
              </div>
            </div>

            <CardContent className="p-4">
              {/* Profile Picture */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  {profile.profilePicture ? (
                    <ImageWithFallback
                      src={profile.profilePicture}
                      alt={getFullName(profile)}
                      className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                      <AvatarFallback className={currentTheme.gradient}>
                        <span className="text-2xl text-white">
                          {getInitials(profile)}
                        </span>
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="text-center space-y-2">
                <h3 className="text-lg" style={{ color: currentTheme.primary }}>
                  {getFullName(profile)}
                </h3>
                <p className="text-sm text-gray-600">{profile.age} years</p>

                {/* Details */}
                <div className="space-y-1 text-sm text-gray-600">
                  {profile.city && (
                    <div className="flex items-center justify-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{profile.city}</span>
                    </div>
                  )}
                  {profile.profession && (
                    <div className="flex items-center justify-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      <span>{profile.profession}</span>
                    </div>
                  )}
                  {profile.education && (
                    <div className="flex items-center justify-center gap-1">
                      <GraduationCap className="h-3 w-3" />
                      <span>{profile.education}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInterest(profile._id);
                    }}
                  >
                    <Heart className="h-4 w-4" />
                    Interest
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 gap-1"
                    style={{ backgroundColor: currentTheme.primary }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessage(profile._id);
                    }}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Message
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {liveProfiles.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg text-gray-600 mb-2">No Live Profiles</h3>
            <p className="text-sm text-gray-500">
              There are no members currently active on the platform.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}